import * as Algorithms from '../algorithms';
import sentinel2Thumb from '../resources/Sentinel-2.jpg';
import landsatThumb from '../resources/Landsat.jpg';

/* Single Satellite Mission Constructor */
const Mission = (
  name,
  shortname,
  cycle,
  startYear,
  endYear,
  opticalResolution,
  bands,
  vizParams,
  properties,
  algorithms,
  fallbackMission = null,
) => {
  /* Unite params */
  const union = {
    name,
    shortname,
    cycle,
    startYear,
    endYear,
    opticalResolution,
    bands,
    vizParams,
    properties,
    fallbackMission,
  };

  /*
    Bind each algorithm to the collection (mission)
    as all of them are built on a currying manner.
    method = mission => (...) => {}
             ^^^^^^^^^^
  */
  const boundAlgorithms = {};

  if (!algorithms) {
    console.error('Mission created without algorithms:', { name, shortname });
  } else {
    Object.keys(algorithms).forEach((key) => {
      if (typeof algorithms[key] !== 'function') {
        console.error(`Algorithm "${key}" is not a function in mission ${shortname}:`, algorithms[key]);
      } else {
        boundAlgorithms[key] = algorithms[key](union);
      }
    });
  }

  // Object.keys(algorithms).forEach((key) => {
  //   boundAlgorithms[key] = algorithms[key](union);
  // });

  return { ...union, algorithms: boundAlgorithms };
};

/* Satellite Mission Collection */
const MissionCollection = (name, provider, image, missions, enabled = false, surfaceReflectance) => {
  /* Unite params */
  const union = {
    name,
    provider,
    image,
    missions,
    enabled,
    surfaceReflectance,
  };

  /* Create a summary of this Mission Collection */
  const summary = {
    cycle: union.missions.reduce((last, mission) => mission.cycle, null),

    startYear: union.missions.reduce((last, mission) => {
      return last ? Math.min(last, mission.startYear) : mission.startYear;
    }, null),

    endYear: union.missions.some((mission) => mission.endYear === null)
      ? null
      : union.missions.reduce((last, mission) => {
          return last ? Math.max(last, mission.endYear) : mission.endYear;
        }, null),

    opticalResolution: union.missions.reduce((last, mission) => mission.opticalResolution, null),
  };

  /* Construct methods */
  const methods = {
    /* Retrieve mission by name */
    get: (name) => {
      const type = window.sessionStorage.getItem('typeSatellite') || 'TOA';
      const missions = type === 'TOA' ? union.missions : union.surfaceReflectance;
      console.log('Retrieving mission with name:', name, 'from missions:', missions);
      console.log('Mission collection:', union);
      const [first] = missions.filter((mission) => mission.name === name);
      return first;
    },
  };

  return { ...union, summary, ...methods };
};

export const fallback = {
  landsat5_t2_toa: Mission(
    'LANDSAT/LT05/C02/T1_TOA',
    'LT05',
    16,
    1984,
    2011,
    30,
    { red: 'B3', green: 'B2', blue: 'B1', nir: 'B4', swir: 'B5', qa: 'QA_PIXEL' },
    { min: 0.0, max: 0.4 },
    { cloudCoverProperty: 'CLOUD_COVER' },
    {
      queryAvailable: Algorithms.Satellite.LandsatTOA.queryAvailable,
      getAvailable: Algorithms.Satellite.LandsatTOA.getAvailable,
      acquire: Algorithms.Satellite.LandsatTOA.acquire,
    },
  ),
  landsat7_t2_toa: Mission(
    'LANDSAT/LE07/C02/T1_TOA',
    'LE07',
    16,
    1999,
    2024,
    30,
    { red: 'B3', green: 'B2', blue: 'B1', nir: 'B4', swir: 'B5', swir2: 'B7', qa: 'QA_PIXEL' },
    { min: 0.0, max: 0.4 },
    { cloudCoverProperty: 'CLOUD_COVER' },
    {
      queryAvailable: Algorithms.Satellite.LandsatTOA.queryAvailable,
      getAvailable: Algorithms.Satellite.LandsatTOA.getAvailable,
      acquire: Algorithms.Satellite.LandsatTOA.acquire,
    },
  ),
  landsat8_t2_toa: Mission(
    'LANDSAT/LC08/C02/T1_TOA',
    'LC08',
    16,
    2013,
    null,
    30,
    { red: 'B4', green: 'B3', blue: 'B2', nir: 'B5', swir: 'B6', qa: 'QA_PIXEL' },
    { min: 0.0, max: 0.4 },
    { cloudCoverProperty: 'CLOUD_COVER' },
    {
      queryAvailable: Algorithms.Satellite.LandsatTOA.queryAvailable,
      getAvailable: Algorithms.Satellite.LandsatTOA.getAvailable,
      acquire: Algorithms.Satellite.LandsatTOA.acquire,
    },
  ),
  landsat9_t2_toa: Mission(
    'LANDSAT/LC09/C02/T1_TOA',
    'LC09',
    16,
    2021,
    null,
    30,
    { red: 'B4', green: 'B3', blue: 'B2', nir: 'B5', swir: 'B6', qa: 'QA_PIXEL' },
    { min: 0.0, max: 0.4 },
    { cloudCoverProperty: 'CLOUD_COVER' },
    {
      queryAvailable: Algorithms.Satellite.LandsatTOA.queryAvailable,
      getAvailable: Algorithms.Satellite.LandsatTOA.getAvailable,
      acquire: Algorithms.Satellite.LandsatTOA.acquire,
    },
  ),
};

export const fallback_sr = {
  landsat5_t1: Mission(
    'LANDSAT/LT05/C02/T1_L2',
    'LT05',
    16,
    1984,
    2012,
    30,
    {
      red: 'SR_B3',
      green: 'SR_B2',
      blue: 'SR_B1',
      nir: 'SR_B4',
      swir: 'SR_B5',
      swir2: 'SR_B7',
      qa: 'QA_PIXEL',
    },
    {
      min: 0.0,
      max: 0.3,
      gain: '1,1,1',
    },
    { cloudCoverProperty: 'CLOUD_COVER' },
    {
      queryAvailable: Algorithms.Satellite.Landsat.queryAvailable,
      getAvailable: Algorithms.Satellite.Landsat.getAvailable,
      acquire: Algorithms.Satellite.Landsat.acquire,
    },
  ),
  landsat7_t1: Mission(
    'LANDSAT/LE07/C02/T1_L2',
    'LE07',
    16,
    1999,
    2024,
    30,
    {
      red: 'SR_B3',
      green: 'SR_B2',
      blue: 'SR_B1',
      nir: 'SR_B4',
      swir: 'SR_B5',
      swir2: 'SR_B7',
      qa: 'QA_PIXEL',
    },
    {
      min: 0.0,
      max: 0.3,
      gain: '1,1,1',
    },
    { cloudCoverProperty: 'CLOUD_COVER' },
    {
      queryAvailable: Algorithms.Satellite.Landsat.queryAvailable,
      getAvailable: Algorithms.Satellite.Landsat.getAvailable,
      acquire: Algorithms.Satellite.Landsat.acquire,
    },
  ),
  landsat8_t1: Mission(
    'LANDSAT/LC08/C02/T1_L2',
    'LC08',
    16,
    2013,
    null,
    30,
    {
      red: 'SR_B4',
      green: 'SR_B3',
      blue: 'SR_B2',
      nir: 'SR_B5',
      swir: 'SR_B6',
      swir2: 'SR_B7',
      qa: 'QA_PIXEL',
      aer: 'SR_B1',
    },
    {
      min: 0.0,
      max: 0.3,
      gain: '1,1,1',
    },
    { cloudCoverProperty: 'CLOUD_COVER' },
    {
      queryAvailable: Algorithms.Satellite.Landsat.queryAvailable,
      getAvailable: Algorithms.Satellite.Landsat.getAvailable,
      acquire: Algorithms.Satellite.Landsat.acquire,
    },
  ),
  landsat9_t1: Mission(
    'LANDSAT/LC09/C02/T1_L2',
    'LC09',
    16,
    2021,
    null,
    30,
    {
      red: 'SR_B4',
      green: 'SR_B3',
      blue: 'SR_B2',
      nir: 'SR_B5',
      swir: 'SR_B6',
      qa: 'QA_PIXEL',
    },
    {
      min: 0.0,
      max: 0.3,
      gain: '1,1,1',
    },
    { cloudCoverProperty: 'CLOUD_COVER' },
    {
      queryAvailable: Algorithms.Satellite.Landsat.queryAvailable,
      getAvailable: Algorithms.Satellite.Landsat.getAvailable,
      acquire: Algorithms.Satellite.Landsat.acquire,
    },
  ),
};

export const getStandardSatellites = (includesLandsat7 = false) => {
  return [
    /* SENTINEL 2 */
    MissionCollection(
      'Sentinel-2',
      'ESA',
      sentinel2Thumb,
      [
        Mission(
          'COPERNICUS/S2_HARMONIZED' /* TOA */,
          'S2',
          5,
          2015,
          null,
          10,
          {
            blue: 'B2',
            green: 'B3',
            red: 'B4',
            nir: 'B8',
            swir: 'B11',
            qa: 'QA60',
          },
          { max: 3000, min: 0 },
          { cloudCoverProperty: 'CLOUDY_PIXEL_PERCENTAGE' },
          {
            queryAvailable: Algorithms.Satellite.Sentinel.queryAvailable,
            getAvailable: Algorithms.Satellite.Sentinel.getAvailable,
            acquire: Algorithms.Satellite.Sentinel.acquire,
          },
        ),
      ],
      true,
      [
        Mission(
          'COPERNICUS/S2_SR_HARMONIZED' /* SR */,
          'S2_SR',
          10,
          2017,
          null,
          10,
          {
            blue: 'B2',
            green: 'B3',
            red: 'B4',
            nir: 'B8',
            swir: 'B11',
            swir2: 'B12',
            qa: 'QA60',
            aer: 'B1',
            rededge1: 'B5',
            rededge2: 'B6',
            rededge3: 'B7',
            nir2: 'B8A',
            waterVapor: 'B9',
            cirrus: 'B10',
            scl: 'SCL',
            aot: 'AOT',
            wvp: 'WVP',
            msk_cldprb: 'MSK_CLDPRB',
            msk_snwprb: 'MSK_SNWPRB',
          },
          {
            max: 0.3,
            min: 0,
          },
          { cloudCoverProperty: 'CLOUDY_PIXEL_PERCENTAGE' },
          {
            queryAvailable: Algorithms.Satellite.Sentinel.queryAvailable,
            getAvailable: Algorithms.Satellite.Sentinel.getAvailable,
            acquire: Algorithms.Satellite.Sentinel.acquire,
          },
        ),
      ],
    ),

    /* LANDSATS */
    MissionCollection(
      'Landsat',
      'USGS/NASA',
      landsatThumb,
      [
        fallback.landsat5_t2_toa,
        ...(includesLandsat7 ? [fallback.landsat7_t2_toa] : []),
        fallback.landsat8_t2_toa,
        fallback.landsat9_t2_toa,
      ] /* TOA */,
      true,
      [
        fallback_sr.landsat5_t1,
        ...(includesLandsat7 ? [fallback_sr.landsat7_t1] : []),
        fallback_sr.landsat8_t1,
        fallback_sr.landsat9_t1,
      ] /* SR */,
    ),
  ];
};

/* Index reserved for the combined collection below — the standard flows only
   ever dispatch 0 (Sentinel-2) or 1 (Landsat). */
export const COMBINED_SATELLITE_INDEX = 2;

/* Shore-S only: every Landsat + Sentinel-2 mission in a single collection, so
   the per-mission pipeline (availability, thumbnails, shoreline derivation)
   runs over both satellites at once. Sentinel goes last on purpose: the
   collection `summary` keeps the last mission's cycle/resolution, so the
   chooser card shows the best values (5 days / 10 m). */
export const getCombinedSatellite = (includesLandsat7 = false) => {
  const [sentinel, landsat] = getStandardSatellites(includesLandsat7);
  return MissionCollection(
    'Landsat + Sentinel-2',
    'USGS/NASA + ESA',
    sentinel2Thumb,
    [...landsat.missions, ...sentinel.missions],
    true,
    [...landsat.surfaceReflectance, ...sentinel.surfaceReflectance],
  );
};

export const get = (index, includesLandsat7 = false) => {
  if (index === COMBINED_SATELLITE_INDEX) return getCombinedSatellite(includesLandsat7);
  return getStandardSatellites(includesLandsat7)[index];
};
